const MINDSET_REVIEW_FIELDS = [
  { key: 'emotions', label: 'Mindset', format: (value) => value.join(', ') },
  { key: 'composure', label: 'Composure', format: (value) => `${value} / 5` },
  { key: 'execution', label: 'Execution' },
  { key: 'learned', label: 'What I learned' },
  { key: 'distraction', label: 'What distracted me' },
  { key: 'improvement', label: 'What I could improve' },
];

export function hasContextValue(value) {
  if (value === undefined || value === null || value === '') return false;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === 'object') return Object.keys(value).length > 0;
  return true;
}

export function formatContextValue(value) {
  if (!hasContextValue(value)) return 'N/A';
  if (value === true || value === 'true') return 'Yes';
  if (value === false || value === 'false') return 'No';
  if (Array.isArray(value)) return value.map(formatContextValue).join(', ');
  if (typeof value === 'object') {
    return Object.entries(value)
      .filter(([, nestedValue]) => hasContextValue(nestedValue))
      .map(([key, nestedValue]) => `${key}: ${formatContextValue(nestedValue)}`)
      .join('; ');
  }
  return String(value);
}

export function getMindsetExecutionRows(entryOrReview) {
  const review = entryOrReview?.mindsetExecution || entryOrReview;
  if (!review || typeof review !== 'object' || Array.isArray(review)) return [];

  return MINDSET_REVIEW_FIELDS.flatMap((field) => {
    const value = review[field.key];
    if (!hasContextValue(value)) return [];
    return [{
      key: field.key,
      label: field.label,
      value: field.format ? field.format(value) : formatContextValue(value),
    }];
  });
}

export function buildAiContextPrompt(fieldDefinitions = { tradeEnv: [] }) {
  const environmentFields = (fieldDefinitions.tradeEnv || [])
    .filter((field) => field?.enabled)
    .map((field) => field.label)
    .filter(Boolean);

  const environmentLine = environmentFields.length > 0
    ? environmentFields.join(', ')
    : 'Any Trade Environment fields included in the pasted records';

  return `You are my trading-journal analyst. Analyze only the records I paste after this message.

DATA FORMAT
- Each record starts with a date heading and contains the fields that were saved for that trade.
- Core fields can include account, title, rating, ticker, dates, entry/exit time, duration, P&L, R:R, session, economic release, PD array, target, notes, and screenshot count.
- Trade Environment fields currently include: ${environmentLine}.
- V3 review fields can include mindset/emotions, composure (1-5), execution quality, what I learned, what distracted me, and what I could improve.
- "N/A" means the trader did not record that value. Do not treat it as No, zero, or a failed rule.

ANALYSIS RULES
1. Keep accounts separate unless I explicitly ask for a combined view.
2. Never invent missing values, causal claims, or hypothetical savings.
3. Distinguish correlation from proof. Describe small samples as early signals, not established edges.
4. Use the saved custom-field labels exactly as written.
5. When calculating stats, state the sample size and show the calculation scope.
6. Treat screenshots as attached evidence only when I actually provide the images; a screenshot count alone is not visual evidence.
7. For loss classifications, say losses were "tagged avoidable" only when the saved classification supports that wording.

DEFAULT RESPONSE
- Scope analyzed
- Performance summary
- Strongest and weakest patterns
- Trade Environment breakdowns
- Mindset and execution patterns
- Trades worth reviewing, with dates and reasons
- Data gaps or fields that need more samples
- One practical focus for the next review period

Use concise tables when comparing categories. Ask what account or date range I want if my request is ambiguous.`;
}
